import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Product } from '@harbor/shared';
import { useApi } from '../lib/hooks.js';
import { money, stockLabel } from '../lib/format.js';
import { useCart } from '../state/cart.js';
import { Alert, Badge, Button, Card, EmptyState, Skeleton } from '../components/ui.js';
import { ProductCard } from '../components/ProductCard.js';

interface ProductResponse {
  product: Product;
  related: Product[];
}

export function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addItem, mutating } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [adding, setAdding] = useState(false);

  const { data, error, loading } = useApi<ProductResponse>(slug ? `/api/products/${slug}` : null);

  if (loading && !data) {
    return (
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Skeleton className="aspect-square w-full rounded-2xl" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 sm:px-6">
        <EmptyState
          title="We could not find that product"
          description={error.message}
          action={
            <Button variant="secondary" onClick={() => navigate('/')}>
              Back to the catalogue
            </Button>
          }
        />
      </div>
    );
  }

  if (!data) return null;

  const { product, related } = data;
  const stock = stockLabel(product.stock);
  const soldOut = product.stock <= 0;
  const image = product.images[activeImage] ?? product.images[0];
  const maxQuantity = Math.min(product.stock, 20);

  async function handleAdd() {
    setAdding(true);
    try {
      await addItem(product.id, quantity);
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <nav aria-label="Breadcrumb" className="mb-8 text-sm text-muted">
        <ol className="flex items-center gap-2">
          <li>
            <Link to="/" className="transition-colors hover:text-ink">
              Catalogue
            </Link>
          </li>
          {product.category && (
            <>
              <li aria-hidden="true">/</li>
              <li>
                <Link
                  to={`/?category=${product.category.slug}`}
                  className="transition-colors hover:text-ink"
                >
                  {product.category.name}
                </Link>
              </li>
            </>
          )}
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="truncate text-ink">
            {product.name}
          </li>
        </ol>
      </nav>

      <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
        <div className="space-y-3">
          <div className="aspect-square overflow-hidden rounded-2xl border border-line bg-sunken">
            {image ? (
              <img
                src={image.url}
                alt={image.alt}
                className="size-full object-cover"
                fetchPriority="high"
                decoding="async"
              />
            ) : (
              <div className="flex size-full items-center justify-center text-sm text-subtle">
                No image available
              </div>
            )}
          </div>

          {product.images.length > 1 && (
            <div className="flex gap-3" role="tablist" aria-label="Product images">
              {product.images.map((entry, index) => (
                <button
                  key={entry.key}
                  type="button"
                  role="tab"
                  aria-selected={index === activeImage}
                  onClick={() => setActiveImage(index)}
                  className={[
                    'size-20 overflow-hidden rounded-lg border transition-colors',
                    index === activeImage ? 'border-accent' : 'border-line hover:border-line-strong',
                  ].join(' ')}
                >
                  <img src={entry.url} alt="" className="size-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {product.category && (
            <p className="text-xs font-medium uppercase tracking-wide text-subtle">
              {product.category.name}
            </p>
          )}

          <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">{product.name}</h1>

          <p className="mt-3 text-base leading-relaxed text-muted">{product.summary}</p>

          <div className="mt-6 flex flex-wrap items-baseline gap-3">
            <span className="tabular text-3xl font-semibold">
              {money(product.priceMinor, product.currency)}
            </span>
            {product.compareAtMinor !== null && product.compareAtMinor > product.priceMinor && (
              <>
                <span className="tabular text-lg text-subtle line-through">
                  {money(product.compareAtMinor, product.currency)}
                </span>
                <Badge tone="accent">
                  Save {money(product.compareAtMinor - product.priceMinor, product.currency)}
                </Badge>
              </>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Badge tone={stock.tone}>{stock.text}</Badge>
            <span className="tabular text-xs text-subtle">SKU {product.sku}</span>
          </div>

          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center rounded-lg border border-line">
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                  disabled={soldOut || quantity <= 1}
                  aria-label="Decrease quantity"
                  className="flex size-11 items-center justify-center text-muted transition-colors hover:text-ink disabled:opacity-40"
                >
                  −
                </button>
                <span className="tabular w-10 text-center" aria-live="polite">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.min(maxQuantity, value + 1))}
                  disabled={soldOut || quantity >= maxQuantity}
                  aria-label="Increase quantity"
                  className="flex size-11 items-center justify-center text-muted transition-colors hover:text-ink disabled:opacity-40"
                >
                  +
                </button>
              </div>

              <Button
                size="lg"
                className="flex-1"
                disabled={soldOut || mutating}
                loading={adding}
                onClick={() => void handleAdd()}
              >
                {soldOut ? 'Sold out' : `Add ${quantity} to basket`}
              </Button>
            </div>

            {!soldOut && product.stock <= 5 && (
              <Alert tone="warning" title="Almost gone">
                {product.stock} unit{product.stock === 1 ? '' : 's'} left. Stock is reserved when you
                check out, not when you add to the basket.
              </Alert>
            )}
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <Assurance title="Free shipping" body="On orders over $75" />
            <Assurance title="10-year spares" body="Every part still sold" />
            <Assurance title="30-day return" body="Including assembled items" />
          </div>

          <Card className="mt-8 p-6">
            <h2 className="text-sm font-semibold">Details</h2>
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-muted">
              {product.description}
            </p>

            {product.tags.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-1.5 border-t border-line pt-5">
                {product.tags.map((tag) => (
                  <Badge key={tag} tone="neutral">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="text-lg font-semibold tracking-tight">More in {product.category?.name}</h2>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((entry) => (
              <ProductCard key={entry.id} product={entry} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Assurance({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-3.5">
      <p className="text-xs font-medium text-ink">{title}</p>
      <p className="mt-0.5 text-xs text-subtle">{body}</p>
    </div>
  );
}
